'use client'

import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdConfirmationNumber,
    MdAdd,
    MdEdit,
    MdDelete,
    MdSearch,
    MdClose,
} from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import ConfirmModal from '@/components/confirm-modal'
import PaginationControls from '@/components/pagination/PaginationControls'
import {
    getCoupons,
    deleteCoupon,
    Coupon,
} from '@/lib/api/commerce/coupons'

function getStatus(coupon: Coupon) {
    const now = new Date()
    if (!coupon.active) return { label: 'Inativo', class: 'badge-ghost' }
    if (new Date(coupon.endDate) < now)
        return { label: 'Expirado', class: 'badge-warning' }
    if (coupon.maxUses && coupon.currentUses >= coupon.maxUses)
        return { label: 'Esgotado', class: 'badge-error' }
    if (new Date(coupon.startDate) > now)
        return { label: 'Agendado', class: 'badge-info' }
    return { label: 'Ativo', class: 'badge-success' }
}

function formatDiscount(coupon: Coupon) {
    if (coupon.discountType === 'PERCENTAGE') {
        return `${coupon.discountValue}%`
    }
    return `R$ ${(coupon.discountValue / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatDate(dateStr: string) {
    return new Date(dateStr).toLocaleDateString('pt-BR')
}

export default function CouponsListPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'commerce',
        'coupons'
    )
    const [searchTerm, setSearchTerm] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [couponToDelete, setCouponToDelete] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [urlPage, setUrlPage] = useUrlPage()

    const debouncedSearch = useDebounce(searchTerm, 300)

    const filters = useMemo(
        () => ({ search: debouncedSearch.trim() || undefined }),
        [debouncedSearch]
    )

    const fetchFn = useCallback(
        (params: typeof filters & { page: number; limit: number }) => {
            return getCoupons(params)
        },
        [user?.organizationId]
    )

    const {
        items: coupons,
        loading,
        page,
        limit,
        total,
        setPage,
        reload,
    } = usePaginatedPage<Coupon, typeof filters>({
        fetchFn,
        filters,
        limit: 15,
        enabled: !!user?.companyId,
        page: urlPage,
        onPageChange: setUrlPage,
    })

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
    }

    const handleDelete = (couponId: number) => {
        setCouponToDelete(couponId)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!couponToDelete) return

        setDeleting(true)
        try {
            const response = await deleteCoupon(couponToDelete)
            if (response.success) {
                toast.success('Cupom excluído com sucesso')
                reload()
                setShowDeleteModal(false)
                setCouponToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir cupom')
            }
        } catch (error) {
            toast.error('Erro ao excluir cupom')
        } finally {
            setDeleting(false)
        }
    }

    if (loading && coupons.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando cupons...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdConfirmationNumber className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Cupons
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {total}{' '}
                            {total === 1
                                ? 'cupom cadastrado'
                                : 'cupons cadastrados'}
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={() => navigate('/commerce/coupons/new')}
                    >
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">Adicionar</span>
                    </button>
                )}
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <form
                    onSubmit={handleSearch}
                    className="flex items-center gap-2"
                >
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por código, nome..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </form>
            </div>

            {coupons.length === 0 && !loading ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdConfirmationNumber className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhum cupom encontrado'
                                : 'Nenhum cupom cadastrado'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Comece adicionando cupons de desconto'}
                        </p>
                        {!searchTerm && canCreate && (
                            <button
                                onClick={() =>
                                    navigate('/commerce/coupons/new')
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar primeiro cupom
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {coupons.map(coupon => {
                        const status = getStatus(coupon)
                        const filtersInfo: string[] = []
                        if (coupon.category)
                            filtersInfo.push(coupon.category.name)
                        if (coupon.subcategory)
                            filtersInfo.push(coupon.subcategory.name)
                        if (coupon.products && coupon.products.length > 0) {
                            filtersInfo.push(
                                `${coupon.products.length} ${coupon.products.length === 1 ? 'produto' : 'produtos'}`
                            )
                        }

                        return (
                            <div
                                key={coupon.id}
                                className="group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-lg transition-all duration-300"
                            >
                                <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4">
                                    <div className="relative w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 bg-gradient-to-br from-secondary/10 to-secondary/5 rounded-xl flex items-center justify-center">
                                        <MdConfirmationNumber className="text-2xl sm:text-3xl text-secondary/40" />
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2 mb-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h3 className="text-sm sm:text-base font-bold text-base-content font-mono tracking-wide">
                                                    {coupon.code}
                                                </h3>
                                                <span
                                                    className={`badge badge-sm ${status.class}`}
                                                >
                                                    {status.label}
                                                </span>
                                            </div>
                                        </div>

                                        {coupon.name && (
                                            <p className="text-xs sm:text-sm text-base-content/70 mb-1">
                                                {coupon.name}
                                            </p>
                                        )}

                                        <div className="flex flex-wrap items-center gap-1.5 mb-2">
                                            <span className="badge badge-xs sm:badge-sm badge-secondary badge-outline">
                                                {formatDiscount(coupon)}
                                            </span>
                                            <span className="badge badge-xs sm:badge-sm badge-ghost">
                                                {coupon.maxUses
                                                    ? `${coupon.currentUses} / ${coupon.maxUses} usos`
                                                    : `${coupon.currentUses} usos - Ilimitado`}
                                            </span>
                                            {filtersInfo.length > 0 &&
                                                filtersInfo.map((info, idx) => (
                                                    <span
                                                        key={idx}
                                                        className="badge badge-xs sm:badge-sm badge-secondary badge-outline"
                                                    >
                                                        {info}
                                                    </span>
                                                ))}
                                        </div>

                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 pt-2 sm:pt-3 border-t border-base-300/50">
                                            <div className="flex items-center gap-2">
                                                <p className="text-xs text-base-content/60">
                                                    Validade:{' '}
                                                    {formatDate(
                                                        coupon.startDate
                                                    )}{' '}
                                                    -{' '}
                                                    {formatDate(coupon.endDate)}
                                                </p>
                                                {coupon.minimumPurchase &&
                                                    coupon.minimumPurchase >
                                                        0 && (
                                                        <span className="text-xs text-base-content/50">
                                                            | Min: R${' '}
                                                            {(
                                                                coupon.minimumPurchase /
                                                                100
                                                            )
                                                                .toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                                .replace(
                                                                    '.',
                                                                    ','
                                                                )}
                                                        </span>
                                                    )}
                                            </div>

                                            {(canUpdate || canDelete) && (
                                                <div className="flex items-center gap-2">
                                                    {canUpdate && (
                                                        <button
                                                            onClick={() =>
                                                                navigate(
                                                                    `/commerce/coupons/edit/${coupon.id}`
                                                                )
                                                            }
                                                            className="btn btn-xs sm:btn-sm btn-ghost"
                                                            title="Editar"
                                                        >
                                                            <MdEdit className="text-base sm:text-lg" />
                                                        </button>
                                                    )}
                                                    {canDelete && (
                                                        <button
                                                            onClick={() =>
                                                                handleDelete(
                                                                    coupon.id
                                                                )
                                                            }
                                                            className="btn btn-xs sm:btn-sm btn-ghost text-error hover:bg-error/10"
                                                            title="Excluir"
                                                        >
                                                            <MdDelete className="text-base sm:text-lg" />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                    {total > limit && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label="cupom"
                        />
                    )}
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setCouponToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir cupom"
                message="Tem certeza de que deseja excluir este cupom? Esta ação não pode ser desfeita."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
